/**
 * The compact textbook pointer on a chapter card — "Y&F 39.1, 38.4, 39.6",
 * "Y&F 37.1–37.9 · föreläsningsanteckningar". The topic page explains "Y&F"
 * once, in its legend line (`content.topics.legend`).
 */
import type { BookRef } from "@shared/types";
import type { TFunction } from "@client/i18n";

/**
 * Collapses runs of consecutive sections of one chapter, keeping the given
 * order: 37.1, 37.2, 37.3 → 37.1–37.3. A run never crosses a chapter.
 */
export function compressSections(sections: readonly string[]): string[] {
  const out: string[] = [];
  let first = "";
  let last = "";
  let lastChapter = Number.NaN;
  let lastSection = Number.NaN;
  for (const section of sections) {
    const [chapter, sectionNo] = section.split(".").map(Number);
    if (first && chapter === lastChapter && sectionNo === lastSection + 1) {
      last = section;
    } else {
      if (first) out.push(first === last ? first : `${first}–${last}`);
      first = section;
      last = section;
    }
    lastChapter = chapter;
    lastSection = sectionNo;
  }
  if (first) out.push(first === last ? first : `${first}–${last}`);
  return out;
}

/** The pointer text, or null when there is nothing to point at. */
export function formatBookRef(book: BookRef, t: TFunction): string | null {
  const parts: string[] = [];
  if (book.sections.length > 0) {
    parts.push(t("content.topics.book", { sections: compressSections(book.sections).join(", ") }));
  }
  if (book.lectureNotes) parts.push(t("content.book.lectureNotes"));
  return parts.length > 0 ? parts.join(" · ") : null;
}
